import { Button } from "@dash/form";
import { SectionHeader } from "@dash/page";
import { SelectDialog } from "@dash/select";
import { useMemo, useState } from "react";
import type { FC } from "react";
import { MdAdd } from "react-icons/md";
import { useQueryClient } from "react-query";

import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { TicketDetailDto } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";

import { SupportFieldLabel } from "../shared/SupportFieldLabel";
import { SupportTicketAddProductSerialDialog } from "./SupportTicketAddProductSerialDialog";
import { normalizeSupportTicketProductQuantity } from "./supportTicketProductLabel";
import { SupportTicketProductsTable } from "./SupportTicketProductsTable";

type ProductsFieldLayout = "form" | "section";

export type TicketProductDraft = {
  productName: string;
  productionDate: Date | null;
  serialNumber: string | null;
  quantity: number;
};

type Entry = {
  id: string;
  productName: string;
  productionDate: Date | null;
  serialNumber: string | null;
  quantity: number;
};

type ProductUpdate = {
  quantity: number;
  productionDate: Date | null;
  serialNumber: string | null;
};

type CoreProps = {
  entries: Entry[];
  onAddProduct: (productName: string, productionDate: Date | null, serialNumber: string | null, quantity: number) => void;
  onRemoveEntry: (id: string) => void;
  onUpdateEntry: (id: string, updates: ProductUpdate) => void;
  isDisabled?: boolean;
  layout: ProductsFieldLayout;
};

type PendingProduct = {
  productName: string;
  label: string;
};

const SupportTicketProductsFieldCore: FC<CoreProps> = ({
  entries,
  onAddProduct,
  onRemoveEntry,
  onUpdateEntry,
  isDisabled,
  layout,
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState<PendingProduct | null>(null);
  const docsClient = useServiceClient("docs");

  const selectedProductNameSet = useMemo(() => new Set(entries.map((e) => e.productName)), [entries]);
  const hasProducts = entries.length > 0;
  const isFormLayout = layout === "form";
  const sectionGap = isFormLayout ? "0.375rem" : "1rem";

  const openPicker = () => setIsPickerOpen(true);

  const addProductButton = !isDisabled ? (
    <Button
      variant="outline"
      size="small"
      icon={isFormLayout || !hasProducts ? <MdAdd size={16} /> : undefined}
      onClick={openPicker}
    >
      {hasProducts && !isFormLayout ? "Dodaj" : "Dodaj produkt"}
    </Button>
  ) : null;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: sectionGap, width: "100%" }}>
      {isFormLayout ? (
        <SupportFieldLabel>Produkty</SupportFieldLabel>
      ) : (
        <SectionHeader
          isHr
          css={{ h2: { fontSize: "16px" } }}
          actions={hasProducts ? addProductButton ?? undefined : undefined}
        >
          Produkty
        </SectionHeader>
      )}
      {hasProducts && (
        <SupportTicketProductsTable
          entries={entries}
          isDisabled={isDisabled}
          onRemoveEntry={onRemoveEntry}
          onUpdateEntry={onUpdateEntry}
        />
      )}
      {isFormLayout && hasProducts && addProductButton}
      {!hasProducts && addProductButton}
      {isPickerOpen && (
        <SelectDialog
          variant="single-select"
          label="Wybierz produkt"
          noTrigger
          isOpen={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          search={async (searchText) => {
            const results = await docsClient.searchItems({ searchText, isArchived: false });
            const items = results.value?.items ?? [];
            return items
              .filter((item) => {
                const productName = item.itemName?.trim() || item.productId;
                return productName.length > 0 && !selectedProductNameSet.has(productName);
              })
              .map((item) => {
                const productName = item.itemName?.trim() || item.productId;
                return {
                  label: productName,
                  value: productName,
                };
              });
          }}
          onSelect={(option) => {
            if (!option?.value) return;
            setPendingProduct({ productName: option.value, label: option.label });
            setIsPickerOpen(false);
          }}
        />
      )}
      {pendingProduct && (
        <SupportTicketAddProductSerialDialog
          productLabel={pendingProduct.label}
          isOpen
          onClose={() => setPendingProduct(null)}
          onConfirm={(productionDate, serialNumber, quantity) => {
            onAddProduct(pendingProduct.productName, productionDate, serialNumber, quantity);
            setPendingProduct(null);
          }}
        />
      )}
    </div>
  );
};

type CreateProps = {
  value: TicketProductDraft[];
  onChange: (next: TicketProductDraft[]) => void;
  isDisabled?: boolean;
};

export const SupportTicketProductsCreateField: FC<CreateProps> = ({ value, onChange, isDisabled }) => {
  const entries: Entry[] = value.map((p) => ({
    id: p.productName,
    productName: p.productName,
    productionDate: p.productionDate,
    serialNumber: p.serialNumber,
    quantity: normalizeSupportTicketProductQuantity(p.quantity),
  }));

  return (
    <SupportTicketProductsFieldCore
      layout="form"
      entries={entries}
      onAddProduct={(productName, productionDate, serialNumber, quantity) => {
        if (value.some((p) => p.productName === productName)) return;
        onChange([...value, { productName, productionDate, serialNumber, quantity }]);
      }}
      onRemoveEntry={(id) => onChange(value.filter((p) => p.productName !== id))}
      onUpdateEntry={(id, updates) =>
        onChange(value.map((p) => (p.productName === id ? { ...p, ...updates } : p)))
      }
      isDisabled={isDisabled}
    />
  );
};

type EditProps = {
  ticketId: Uuid;
  products: TicketDetailDto["products"];
  isDisabled?: boolean;
};

export const SupportTicketProductsEditField: FC<EditProps> = ({ ticketId, products, isDisabled }) => {
  const queryClient = useQueryClient();
  const support = useClientManager("support");
  const updateMutation = support.updateTicketProducts().useMutation({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
  });
  const isBusy = "isLoading" in updateMutation ? updateMutation.isLoading : false;

  const currentDrafts: TicketProductDraft[] = (products ?? []).map((p) => ({
    productName: p.productName,
    productionDate: p.productionDate ?? null,
    serialNumber: p.serialNumber,
    quantity: normalizeSupportTicketProductQuantity(p.quantity),
  }));

  const commit = (next: TicketProductDraft[]) => {
    updateMutation.mutate({ id: ticketId, products: next });
  };

  const entries: Entry[] = currentDrafts.map((p) => ({
    id: p.productName,
    productName: p.productName,
    productionDate: p.productionDate,
    serialNumber: p.serialNumber,
    quantity: normalizeSupportTicketProductQuantity(p.quantity),
  }));

  return (
    <SupportTicketProductsFieldCore
      layout="section"
      entries={entries}
      onAddProduct={(productName, productionDate, serialNumber, quantity) => {
        if (currentDrafts.some((p) => p.productName === productName)) return;
        commit([
          ...currentDrafts,
          { productName, productionDate, serialNumber, quantity },
        ]);
      }}
      onRemoveEntry={(id) => commit(currentDrafts.filter((p) => p.productName !== id))}
      onUpdateEntry={(id, updates) =>
        commit(currentDrafts.map((p) => (p.productName === id ? { ...p, ...updates } : p)))
      }
      isDisabled={isBusy || isDisabled}
    />
  );
};
